import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ProjectList } from "../Constants/ProjectDetails";
import Title from "../Components/Title";
import { AiOutlineLeft, AiOutlineRight, AiOutlineGithub } from "react-icons/ai";
import { TbWorldWww } from "react-icons/tb";
import Modal from "../Components/Modal";

const IFRAME_W = 1280;
const IFRAME_H = 720;

// Card sizes
const CENTER_W = 560;
const CENTER_H = Math.round(IFRAME_H * (CENTER_W / IFRAME_W));
const SIDE_W = 400;
const SIDE_H = Math.round(IFRAME_H * (SIDE_W / IFRAME_W));

const Project = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const total = ProjectList.length;

  const go = (dir) => {
    setDirection(dir);
    setHovered(false);
    setCurrent((c) => (c + dir + total) % total);
  };

  const getIndex = (offset) => (current + offset + total) % total;
  const activeProject = ProjectList[current];

  const handleShowProject = () => {
    if (activeProject?.clientDemoLink) {
      window.open(activeProject.clientDemoLink, "_blank", "noopener noreferrer");
    } else {
      setShowModal(true);
      document.body.classList.add("overflow-hidden");
    }
  };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 180 : -180, opacity: 0, scale: 0.95 }),
    center: { x: 0, opacity: 1, scale: 1, transition: { duration: 0.42, ease: "easeOut" } },
    exit: (dir) => ({ x: dir > 0 ? -180 : 180, opacity: 0, scale: 0.95, transition: { duration: 0.32, ease: "easeIn" } }),
  };

  return (
    <motion.div
      className="flex justify-center items-center flex-col py-8 mt-8 mx-auto w-full overflow-hidden"
      id="projects"
    >
      {/* ── Hidden iframe cache — all preloaded once ── */}
      <div className="fixed -left-[9999px] -top-[9999px] pointer-events-none" aria-hidden="true">
        {ProjectList.map((p) => {
          const url = p.demoLink || p.clientDemoLink;
          if (!url) return null;
          return (
            <iframe
              key={p.key}
              src={url}
              title={p.name}
              style={{ width: IFRAME_W, height: IFRAME_H, border: "none" }}
              sandbox="allow-scripts allow-same-origin"
            />
          );
        })}
      </div>

      {/* Title */}
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        <Title text="Projects" />
      </motion.div>

      {/* ── Carousel ── */}
      <div className="relative w-full mt-12">

        {/* Desktop — 3 cards in a row */}
        <div className="hidden md:flex items-center justify-center gap-6">

          {/* Prev card */}
          <div className="flex-shrink-0 opacity-50 transition-opacity duration-300" style={{ transform: "scale(0.92)", transformOrigin: "right center" }}>
            <PreviewCard
              project={ProjectList[getIndex(-1)]}
              width={SIDE_W}
              height={SIDE_H}
            />
          </div>

          {/* Active card */}
          <div className="flex-shrink-0 z-10">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <ActiveCard
                  project={activeProject}
                  hovered={hovered}
                  setHovered={setHovered}
                  onClick={handleShowProject}
                  width={CENTER_W}
                  height={CENTER_H}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Next card */}
          <div className="flex-shrink-0 opacity-50 transition-opacity duration-300" style={{ transform: "scale(0.92)", transformOrigin: "left center" }}>
            <PreviewCard
              project={ProjectList[getIndex(1)]}
              width={SIDE_W}
              height={SIDE_H}
            />
          </div>
        </div>

        {/* Mobile — single card */}
        <div className="flex md:hidden items-center justify-center px-4">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={current}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <ActiveCard
                project={activeProject}
                hovered={hovered}
                setHovered={setHovered}
                onClick={handleShowProject}
                width={320}
                height={Math.round(IFRAME_H * (320 / IFRAME_W))}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Arrow buttons */}
        <div className="hidden md:block">
          <button
            onClick={() => go(-1)}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 shadow-lg hover:bg-mainTheme hover:text-white hover:border-mainTheme text-gray-600 dark:text-gray-300 transition-all duration-200"
          >
            <AiOutlineLeft size={22} />
          </button>
          <button
            onClick={() => go(1)}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 shadow-lg hover:bg-mainTheme hover:text-white hover:border-mainTheme text-gray-600 dark:text-gray-300 transition-all duration-200"
          >
            <AiOutlineRight size={22} />
          </button>
        </div>

        {/* Mobile arrows */}
        <div className="flex md:hidden justify-center gap-6 mt-6">
          <button onClick={() => go(-1)} className="p-3 rounded-full bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 shadow-md hover:bg-mainTheme hover:text-white text-gray-600 dark:text-gray-300 transition-all duration-200">
            <AiOutlineLeft size={20} />
          </button>
          <button onClick={() => go(1)} className="p-3 rounded-full bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 shadow-md hover:bg-mainTheme hover:text-white text-gray-600 dark:text-gray-300 transition-all duration-200">
            <AiOutlineRight size={20} />
          </button>
        </div>
      </div>

      {/* Dots */}
      <div className="flex gap-2 mt-8">
        {ProjectList.map((_, i) => (
          <button
            key={i}
            onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); setHovered(false); }}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current ? "w-8 bg-mainTheme" : "w-2 bg-gray-300 dark:bg-zinc-600 hover:bg-fadeMainTheme"
            }`}
          />
        ))}
      </div>
      <p className="mt-3 text-sm text-gray-400 dark:text-zinc-500">{current + 1} / {total}</p>

      {showModal && (
        <Modal
          onClose={() => { setShowModal(false); document.body.classList.remove("overflow-hidden"); }}
          projectKey={activeProject.key}
        />
      )}
    </motion.div>
  );
};

/* ── Active (center) card with hover overlay ── */
const ActiveCard = ({ project, hovered, setHovered, onClick, width, height }) => {
  const url = project?.demoLink || project?.clientDemoLink;
  const scale = width / IFRAME_W;

  return (
    <div
      className="relative rounded-2xl overflow-hidden cursor-pointer border border-gray-200 dark:border-gray-700 bg-white dark:bg-zinc-900 shadow-2xl transition-shadow duration-300"
      style={{ width }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {/* Live preview */}
      <div className="relative overflow-hidden bg-gray-100 dark:bg-zinc-800" style={{ height }}>
        {url ? (
          <>
            <iframe
              src={url}
              title={project.name}
              style={{
                width: IFRAME_W, height: IFRAME_H,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute", top: 0, left: 0,
                border: "none", pointerEvents: "none",
              }}
              sandbox="allow-scripts allow-same-origin"
            />
            <div className="absolute inset-0" />
          </>
        ) : (
          <img src={project?.image} alt={project?.name} className="h-full w-full object-contain p-4" />
        )}
      </div>

      {/* Hover overlay */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6"
        style={{ backgroundColor: hovered ? 'color-mix(in srgb, var(--accent) 90%, transparent)' : 'transparent' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.22 }}
      >
        <h3 className="text-white font-bold text-xl text-center leading-tight">{project?.name}</h3>
        {project?.skills && (
          <div className="flex flex-wrap justify-center gap-1.5">
            {project.skills[0].split(",").map((s, i) => (
              <span key={i} className="text-xs bg-white/20 text-white px-2.5 py-0.5 rounded-full border border-white/30">
                {s.trim()}
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          {project?.codeLink && (
            <button
              onClick={(e) => { e.stopPropagation(); window.open(project.codeLink, "_blank", "noopener noreferrer"); }}
              className="flex items-center gap-1.5 bg-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
              style={{ color: 'var(--accent)' }}
            >
              <AiOutlineGithub size={16} /> Code
            </button>
          )}
          {url && (
            <button
              onClick={(e) => { e.stopPropagation(); window.open(url, "_blank", "noopener noreferrer"); }}
              className="flex items-center gap-1.5 bg-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
              style={{ color: 'var(--accent)' }}
            >
              <TbWorldWww size={16} /> Demo
            </button>
          )}
        </div>
      </motion.div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700">
        <h2 className="font-bold text-base text-gray-800 dark:text-fadeMainTheme truncate">{project?.name}</h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          {project?.skills?.[0]?.split(",")[0]?.trim()} · Click to explore
        </p>
      </div>
    </div>
  );
};

/* ── Side card — live preview, no overlay ── */
const PreviewCard = ({ project, width, height }) => {
  const url = project?.demoLink || project?.clientDemoLink;
  const scale = width / IFRAME_W;

  return (
    <div
      className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-zinc-900 shadow-md"
      style={{ width }}
    >
      <div className="relative overflow-hidden bg-gray-100 dark:bg-zinc-800" style={{ height }}>
        {url ? (
          <iframe
            src={url}
            title={project?.name}
            style={{
              width: IFRAME_W, height: IFRAME_H,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              position: "absolute", top: 0, left: 0,
              border: "none", pointerEvents: "none",
            }}
            sandbox="allow-scripts allow-same-origin"
          />
        ) : (
          <img src={project?.image} alt={project?.name} className="h-full w-full object-contain p-6" />
        )}
      </div>
      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
        <h2 className="font-bold text-sm text-gray-800 dark:text-fadeMainTheme truncate">{project?.name}</h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{project?.skills?.[0]?.split(",")[0]?.trim()}</p>
      </div>
    </div>
  );
};

export default Project;
